import { extractExplanationFromPartialJson } from '@arnold/core';
import { useEffect, useRef, useState } from 'react';
import {
  useAiSurveySummaryQueryQuery,
  useAiSurveySummarySubscriptionSubscription,
  type QuestionWithAnswer,
} from '../../../../generated/hooks';
import { SURVEY_SUMMARY_PROMPT } from '../../../aiSummary/summaryInput';

/** The baseline API only. A failed websocket falls back to the existing HTTP query once. */
export function useSummaryStream(accessToken: string, languageCode: string, answers: QuestionWithAnswer[]) {
  const variables = { accessToken, languageCode, answers, systemMessage: SURVEY_SUMMARY_PROMPT };
  const [fallback, setFallback] = useState(false);
  const subscription = useAiSurveySummarySubscriptionSubscription({ variables, skip: fallback });
  useEffect(() => {
    if (subscription.error) setFallback(true);
  }, [subscription.error]);
  const query = useAiSurveySummaryQueryQuery({ variables, skip: !fallback, fetchPolicy: 'network-only' });
  const source = fallback ? query.data?.aiSurveySummary : subscription.data?.aiSurveySummary;
  const loading = fallback ? query.loading : subscription.loading;
  const error = fallback ? query.error : undefined;
  const streamed = useRef(false);
  if (source && !source.done) streamed.current = true;
  const target = extractExplanationFromPartialJson(source?.text ?? '');
  const text = useStreamingText(target, streamed.current);
  return {
    source,
    text,
    error,
    generating: !error && (loading || !source?.done || text !== target),
    streamed: streamed.current,
  };
}

/** Cancellable, batched smoothing; cached completed summaries render immediately. */
function useStreamingText(target: string, animate: boolean): string {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setText(target);
      return;
    }
    if (text === target) return;
    const timer = window.setTimeout(() => {
      setText((previous) => {
        const start = target.startsWith(previous) ? previous.length : 0;
        return target.slice(0, start + Math.max(1, Math.ceil((target.length - start) / 8)));
      });
    }, 24);
    return () => window.clearTimeout(timer);
  }, [target, text, animate]);
  if (!animate) return target;
  return target.startsWith(text) ? text : '';
}
